/**
 * Small fleet-health pill in the Navbar (OCP-83). Polls
 * `/v1/admin/tenants/health/summary` every 60s; clickable → links to
 * the full Tenant-health dashboard. Renders dot-green when everything
 * is healthy, otherwise compact "fleet: 3 red, 1 yellow" copy.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api';

interface FleetSummary {
  total: number;
  green: number;
  yellow: number;
  red: number;
  inactive: number;
}

const POLL_INTERVAL_MS = 60 * 1000;

export function FleetHealthBadge() {
  const [summary, setSummary] = useState<FleetSummary | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await api<FleetSummary>('/v1/admin/tenants/health/summary');
        if (!cancelled) {
          setSummary(r);
          setError(false);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    };
    void fetchOnce();
    const timer = setInterval(() => {
      void fetchOnce();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  if (error || !summary) {
    return null;
  }

  if (summary.total === 0) {
    return (
      <Link
        to="/tenants/health"
        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        data-testid="fleet-health-badge"
      >
        <span className="block h-2 w-2 rounded-full bg-gray-300" />
        No tenants
      </Link>
    );
  }

  if (summary.red === 0 && summary.yellow === 0) {
    return (
      <Link
        to="/tenants/health"
        className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300 hover:text-emerald-900"
        data-testid="fleet-health-badge"
      >
        <span className="block h-2 w-2 rounded-full bg-emerald-500" />
        Fleet healthy
      </Link>
    );
  }

  const parts: string[] = [];
  if (summary.red > 0) parts.push(`${summary.red} red`);
  if (summary.yellow > 0) parts.push(`${summary.yellow} yellow`);

  return (
    <Link
      to="/tenants/health"
      className="flex items-center gap-1.5 rounded bg-red-50 dark:bg-red-900/30 px-2 py-1 text-xs font-medium text-red-800 dark:text-red-300 hover:bg-red-100"
      data-testid="fleet-health-badge"
    >
      <span
        className={`block h-2 w-2 rounded-full ${summary.red > 0 ? 'bg-red-500' : 'bg-amber-500'}`}
      />
      Fleet: {parts.join(', ')}
    </Link>
  );
}
